import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Box,
  TextField,
  makeStyles,
  Paper,
  CircularProgress,
  Snackbar,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PublishIcon from '@material-ui/icons/Publish';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Header,
  Page,
  Content,
  MarkdownContent,
} from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  editorInput: {
    '& .MuiOutlinedInput-root': {
      alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-inputMultiline': {
      maxHeight: 'calc(100vh - 320px)',
      overflow: 'auto !important',
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  previewPane: {
    height: 'calc(100vh - 320px)',
    overflow: 'auto',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  versionInput: {
    width: 200,
  },
  paneLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    flexShrink: 0,
  },
  currentVersion: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface GqlResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

export const PrepareReleaseNotesPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const graphqlUrl = `${backendUrl}/api/proxy/release-notes/graphql`;

  const initialMarkdown = (location.state as { markdown?: string })?.markdown ?? '';

  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [version, setVersion] = useState('');
  const [setAsCurrent, setSetAsCurrent] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const gql = useCallback(
    async <T,>(query: string, variables?: Record<string, unknown>): Promise<GqlResponse<T>> => {
      const res = await fetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      return res.json();
    },
    [fetch, graphqlUrl],
  );

  // Fetch current release on mount
  useEffect(() => {
    const fetchCurrent = async () => {
      try {
        const result = await gql<{ currentRelease: { version: string } | null }>(`
          query {
            currentRelease {
              version
            }
          }
        `);
        setCurrentVersion(result.data?.currentRelease?.version ?? null);
      } catch {
        // ignore – just won't show current version
      } finally {
        setLoadingCurrent(false);
      }
    };
    fetchCurrent();
  }, [gql]);

  const canPublish = markdown.trim().length > 0 && version.trim().length > 0;

  const handlePublish = async () => {
    if (!canPublish) return;
    setPublishing(true);

    try {
      // Create the release
      const createResult = await gql<{ createRelease: { id: number; version: string } }>(`
        mutation CreateRelease($version: String!, $changelog: String!) {
          createRelease(version: $version, changelog: $changelog) {
            id
            version
          }
        }
      `, { version: version.trim(), changelog: markdown });

      if (createResult.errors) {
        setSnackbar({ open: true, message: `Publish failed: ${createResult.errors[0].message}`, severity: 'error' });
        return;
      }

      const publishedVersion = createResult.data!.createRelease.version;

      // Set as current if checked
      if (setAsCurrent) {
        const currentResult = await gql<{ setCurrentRelease: { version: string } }>(`
          mutation SetCurrentRelease($version: String!) {
            setCurrentRelease(version: $version) {
              version
            }
          }
        `, { version: publishedVersion });

        if (currentResult.errors) {
          setSnackbar({ open: true, message: `Published but failed to set as current: ${currentResult.errors[0].message}`, severity: 'error' });
          return;
        }

        setCurrentVersion(publishedVersion);
      }

      setSnackbar({ open: true, message: `Release ${publishedVersion} published${setAsCurrent ? ' and set as current' : ''}!`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Publish failed: network error', severity: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="Prepare Release Notes"
        subtitle="Edit and review release notes before publishing"
      />
      <Content>
        <Box className={classes.toolbar}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/release-notes')}
          >
            Back
          </Button>

          <Box className={classes.currentVersion}>
            <Typography variant="body2" color="textSecondary">
              Current version:
            </Typography>
            {loadingCurrent ? (
              <CircularProgress size={14} />
            ) : currentVersion ? (
              <Chip label={currentVersion} size="small" color="default" />
            ) : (
              <Typography variant="body2" color="textSecondary">none</Typography>
            )}
          </Box>

          <TextField
            className={classes.versionInput}
            label="New Version"
            placeholder="e.g. 2.4.0"
            variant="outlined"
            size="small"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={setAsCurrent}
                onChange={(e) => setSetAsCurrent(e.target.checked)}
                size="small"
                color="primary"
              />
            }
            label="Set as current"
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
            onClick={handlePublish}
            disabled={!canPublish || publishing}
          >
            Publish
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" className={classes.paneLabel}>
              Edit
            </Typography>
            <TextField
              className={classes.editorInput}
              multiline
              fullWidth
              variant="outlined"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2" className={classes.paneLabel}>
              Preview
            </Typography>
            <Paper className={classes.previewPane} variant="outlined">
              {markdown.trim() ? (
                <MarkdownContent content={markdown} />
              ) : (
                <Typography color="textSecondary" variant="body2">
                  Start typing to see a preview...
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Content>
    </Page>
  );
};
